import * as i0 from '@angular/core';
import { NgModule, Injector, Injectable, Inject } from '@angular/core';
import { __decorate, __metadata } from 'tslib';
import * as i1 from '@angular/common/http';
import { HttpClient } from '@angular/common/http';
import { AutoSubscription } from '@upr-web-primer/app-utils';
import * as i2 from '@upr-web-primer/gds-common-utility';
import { GdsCommonUtilityService } from '@upr-web-primer/gds-common-utility';
import { GdsFullImageCardService } from '@upr-web-primer/gds-full-image-card';
import * as i3 from '@upr-web-primer/locale';
import { LocaleService } from '@upr-web-primer/locale';
import * as i6 from '@upr-web-primer/ms-places';
import { MsPlacesService, MsPlacesCommonUtilityService } from '@upr-web-primer/ms-places';
import * as i4 from '@upr-web-primer/page-labels';
import { PageLabelsResolver } from '@upr-web-primer/page-labels';
import * as i5 from '@upr-web-primer/storage';
import { SessionStorageService } from '@upr-web-primer/storage';
import { get, isEmpty, map, forEach, set } from 'lodash-es';
import { DateTime } from 'luxon';
import moment from 'moment-timezone';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { catchError, distinctUntilChanged } from 'rxjs/operators';

class GdsPoiModule {
}
GdsPoiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsPoiModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
GdsPoiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsPoiModule });
GdsPoiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsPoiModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsPoiModule, decorators: [{
            type: NgModule
        }] });

let GdsPoiService = class GdsPoiService {
    constructor(injector, http, gdsCommonUtilities, localeService, pageLabelsResolver, sessionStorageService, msPlaces, msPlacesUtilities, appDefaults) {
        this.injector = injector;
        this.http = http;
        this.gdsCommonUtilities = gdsCommonUtilities;
        this.localeService = localeService;
        this.pageLabelsResolver = pageLabelsResolver;
        this.sessionStorageService = sessionStorageService;
        this.msPlaces = msPlaces;
        this.msPlacesUtilities = msPlacesUtilities;
        this.appDefaults = appDefaults;
        this.relatedTiles$ = new BehaviorSubject(null);
        this.awaitingComponents = [];
        this.isReady = new BehaviorSubject(false);
        this.timeZone = this.appDefaults.UPR_TIME_ZONE;
        this.pageLabelsResolver.get().then((pageLabels) => {
            this.pageLabels = pageLabels;
            this.customTimeFormat = get(this.pageLabels, 'customTimeFormat', '');
            this.hoursDisplayFormat = get(this.pageLabels, 'hoursDisplayFormat', '');
            this.isReady.next(true);
        });
    }
    get fullImageCardService() {
        if (!this._fullImageCardService) {
            this._fullImageCardService = this.injector.get(GdsFullImageCardService);
        }
        return this._fullImageCardService;
    }
    formatHours(openTime, closeTime) {
        let hours = '';
        const amOrPm = this.timeOfDay(openTime, closeTime);
        const openHours = this.formatByTimeZone(openTime);
        const closeHours = this.formatByTimeZone(closeTime);
        if (!!this.hoursDisplayFormat) {
            hours = this.gdsCommonUtilities.replaceHashCodes(this.hoursDisplayFormat, {
                openhours: openHours,
                closehours: closeHours,
            });
        }
        else if (['am-pm', 'pm-am'].includes(amOrPm)) {
            hours =
                openHours +
                    ' ' +
                    amOrPm.split('-')[0] +
                    '-' +
                    closeHours +
                    ' ' +
                    amOrPm.split('-')[1];
        }
        else {
            hours = openHours + '-' + closeHours + ' ' + amOrPm;
        }
        return hours;
    }
    fetchHoursByVenueId(id) {
        const base = this.appDefaults.UPR_MOBILE_API_BASEURL +
            this.slashOrNoSlash(this.appDefaults.UPR_MOBILE_API_BASEURL) +
            `Venues/${id}/Hours`;
        return this.http
            .get(base, {
            headers: {
                'X-UNIWebService-ApiKey': this.appDefaults.UPR_MOBILE_APIKEY,
                'X-UNIWebService-Token': this.appDefaults.UPR_MOBILE_TOKEN,
            },
            params: {
                city: this.appDefaults.UPR_MOBILE_CITY,
            },
        })
            .pipe(catchError((error) => {
            throw new Error(`Failed to retrieve hours for venue id: '${id}'\n ${error.message}`);
        }));
    }
    slashOrNoSlash(url) {
        if (url[url.length - 1] !== '/')
            return '/';
        return '';
    }
    fetchVenues() {
        const url = this.appDefaults.UPR_MOBILE_API_BASEURL +
            this.slashOrNoSlash(this.appDefaults.UPR_MOBILE_API_BASEURL) +
            'PointsOfInterest/DiningLocations';
        return this.http.get(url, {
            headers: {
                'X-UNIWebService-ApiKey': this.appDefaults.UPR_MOBILE_APIKEY,
                'X-UNIWebService-Token': this.appDefaults.UPR_MOBILE_TOKEN,
            },
            params: {
                city: this.appDefaults.UPR_MOBILE_CITY,
                pageSize: 'All',
            },
        });
    }
    fetchVenueHours() {
        const url = this.appDefaults.UPR_MOBILE_API_BASEURL +
            this.slashOrNoSlash(this.appDefaults.UPR_MOBILE_API_BASEURL) +
            'Venues';
        return this.http.get(url, {
            headers: {
                'X-UNIWebService-ApiKey': this.appDefaults.UPR_MOBILE_APIKEY,
                'X-UNIWebService-Token': this.appDefaults.UPR_MOBILE_TOKEN,
            },
            params: {
                city: this.appDefaults.UPR_MOBILE_CITY,
                pageSize: 'All',
            },
        });
    }
    formatByTimeZone(time) {
        let formattedTime = '';
        if (this.customTimeFormat && this.hoursDisplayFormat) {
            formattedTime = moment
                .tz(time, this.timeZone)
                .format(this.customTimeFormat.replace('A', '').trim());
        }
        else {
            formattedTime = moment.tz(time, this.timeZone).get('minutes')
                ? moment.tz(time, this.timeZone).format('h:mm')
                : moment.tz(time, this.timeZone).format('h');
        }
        return formattedTime;
    }
    getMsPlaceHours(component, placeId, venueId) {
        this.msPlaces
            .getPlaceById(placeId)
            .then((place) => {
            const periods = get(place, 'place_hours.periods');
            const locale = this.localeService.getFromStorage();
            const localeString = `${locale.language}-${locale.country.toUpperCase()}`;
            if (periods) {
                const customDateFormat = get(this.pageLabels, 'customDateFormat', '');
                const luxonTimeFormat = this.customTimeFormat.replace('A', 'a');
                const parsedHours = this.msPlacesUtilities.getWeekHours(place, (hours) => {
                    const { open, close } = hours;
                    const openLocale = open.setLocale(localeString);
                    const closeLocale = close.setLocale(localeString);
                    const formattedOpenTime = this.gdsCommonUtilities.createTimeElement(openLocale, {
                        format: luxonTimeFormat,
                    });
                    const formattedCloseTime = this.gdsCommonUtilities.createTimeElement(closeLocale, {
                        format: luxonTimeFormat,
                    });
                    let formattedHours = '';
                    if (!!this.hoursDisplayFormat) {
                        formattedHours = this.gdsCommonUtilities.replaceHashCodes(this.hoursDisplayFormat, {
                            openhours: formattedOpenTime,
                            closehours: formattedCloseTime,
                        });
                    }
                    else {
                        formattedHours = `${formattedOpenTime} - ${formattedCloseTime}`;
                    }
                    let displayDate = '';
                    if (!!customDateFormat) {
                        displayDate = this.gdsCommonUtilities.createTimeElement(openLocale, {
                            datetime: open.toFormat('yyyy-MM-dd'),
                            format: customDateFormat,
                        });
                    }
                    else {
                        displayDate = this.gdsCommonUtilities.createTimeElement(openLocale, {
                            datetime: open.toFormat('yyyy-MM-dd'),
                            locale: DateTime.DATE_FULL,
                        });
                    }
                    const abbreviatedDay = openLocale
                        .toFormat('ccc')
                        .replace('週', '');
                    const wholeDay = openLocale
                        .setLocale(localeString)
                        .toFormat('cccc');
                    return {
                        displayDate,
                        weekday: `<abbr title="${wholeDay}">${abbreviatedDay}</abbr>`,
                        openAndCloseTime: formattedHours,
                    };
                });
                const hoursData = {
                    weekHoursString: get(this.pageLabels, 'GDSVenueHours', ''),
                    hours: parsedHours,
                    hoursLabel: get(this.pageLabels, 'hoursOfOperation', ''),
                };
                component.setAttribute('hoursdata', JSON.stringify(hoursData));
            }
        })
            .catch(() => venueId && this.getDiningHours(component, venueId));
    }
    getDiningHours(component, venueId, placeId) {
        if (placeId) {
            return this.getMsPlaceHours(component, placeId, venueId);
        }
        if (this.appDefaults.UPR_MOBILE_CITY === 'USJ') {
            const venueList = this.sessionStorageService.getItem('VENUES');
            let hoursOfOperation = '';
            let venue = null;
            if (venueList) {
                if (venueId) {
                    venue = venueList.find((venue) => venue.contentId === venueId);
                }
                if (venue?.hoursOfOperation?.length) {
                    hoursOfOperation = venue.hoursOfOperation;
                }
                this.displayDiningHours(component, hoursOfOperation);
            }
            else {
                this.awaitingComponents.push({
                    component,
                    venueId,
                });
                if (this.awaitingComponents.length < 2) {
                    this.fetchVenues().subscribe((data) => {
                        this.sessionStorageService.setItem('VENUES', this.mapVenueHours(data?.Results), {
                            duration: {
                                minutes: 30,
                            },
                        });
                        this.awaitingComponents.forEach((comp) => {
                            this.getDiningHours(comp.component, comp.venueId);
                        });
                        this.awaitingComponents = [];
                    }, (err) => {
                        this.awaitingComponents = [];
                        throw new Error(`Failed to retrieve dining hours: ${err.message}`);
                    });
                }
            }
        }
    }
    mapVenueHours(data) {
        if (isEmpty(data))
            return;
        return data.map((result) => {
            return {
                hoursOfOperation: get(result, 'HoursOfOperation', ''),
                openTime: get(result, 'GeneralOpenTime', ''),
                closeTime: get(result, 'GeneralCloseTime', ''),
                venueStatus: get(result, 'VenueStatus', ''),
                contentId: get(result, 'ExternalIds.ContentId', ''),
            };
        });
    }
    displayDiningHours(component, hoursOfOperation) {
        let hours = '';
        this.pageLabelsResolver.get().then((pageLabels) => {
            if (pageLabels.GDSVenueHours) {
                if (hoursOfOperation &&
                    pageLabels.customDateFormat &&
                    this.customTimeFormat) {
                    hours = this.processTime(hoursOfOperation, pageLabels);
                }
                const hoursData = {
                    weekHoursString: pageLabels.GDSVenueHours,
                    hours,
                    hoursLabel: pageLabels.hoursOfOperation
                        ? pageLabels.hoursOfOperation
                        : '',
                };
                component.hoursdata = hoursData;
            }
        });
    }
    processTime(data, pageLabels) {
        const customDateFormat = pageLabels.customDateFormat.toUpperCase();
        const currentMonthData = data
            .filter((item) => {
            return moment(item.Date).isSameOrAfter(moment(), 'days');
        })
            .slice(0, 7);
        return map(currentMonthData, (date) => ({
            displayDate: moment.parseZone(date.Date).format(customDateFormat),
            weekday: moment.parseZone(date.Date).format('ddd').replace('週', ''),
            openAndCloseTime: moment(date.OpenTimeString).isValid() &&
                moment(date.CloseTimeString).isValid()
                ? this.formatHours(date.OpenTimeString, date.CloseTimeString)
                : pageLabels[date.VenueStatus],
        }));
    }
    async getHours(gdsContent) {
        const section = get(gdsContent, 'section') || get(gdsContent, 'secondSection');
        const sectionList = !Array.isArray(section) ? [section] : section;
        let count = 0;
        return new Promise((resolve) => {
            if (isEmpty(sectionList)) {
                resolve(gdsContent);
            }
            else {
                forEach(sectionList, (sectionData, index) => {
                    const description = get(sectionData, 'description');
                    const venueId = get(sectionData, 'endPoint');
                    const includesParkHours = description?.includes(this.pageLabels['parkHours']);
                    if (!includesParkHours) {
                        count++;
                        if (count === sectionList.length) {
                            resolve(gdsContent);
                        }
                        return;
                    }
                    if (!venueId) {
                        count++;
                        this.setHours(gdsContent, index, this.pageLabels['notAvailable'], true);
                        if (count === sectionList.length) {
                            resolve(gdsContent);
                        }
                        return;
                    }
                    firstValueFrom(this.fetchHoursByVenueId(venueId))
                        .then(async (res) => {
                        count++;
                        if (!res && res.length === 0) {
                            return;
                        }
                        const todaysHours = this.getTodaysHours(res);
                        if (!todaysHours) {
                            gdsContent = await this.setHours(gdsContent, index, this.pageLabels['notAvailable'], true);
                            console.error("Could not retrieve today's hours");
                            if (sectionList.length === count) {
                                resolve(gdsContent);
                            }
                        }
                        const venueStatus = get(todaysHours, 'VenueStatus');
                        if (venueStatus) {
                            gdsContent = await this.setHours(gdsContent, index, venueStatus, true);
                            resolve(gdsContent);
                            return;
                        }
                        const hours = this.formatHours(get(todaysHours, 'OpenTimeString'), get(todaysHours, 'CloseTimeString'));
                        gdsContent = await this.setHours(gdsContent, index, hours, false);
                        if (sectionList.length === count) {
                            resolve(gdsContent);
                        }
                    })
                        .catch(async (error) => {
                        count++;
                        gdsContent = await this.setHours(gdsContent, index, this.pageLabels['notAvailable'], true);
                        console.error(error);
                        if (sectionList.length === count) {
                            resolve(gdsContent);
                        }
                    });
                });
            }
        });
    }
    getTodaysHours(venueHours) {
        const todaysDate = moment().tz(this.timeZone).format('YYYY-MM-DD');
        for (let i = 0; i < venueHours.length; i++) {
            const date = get(venueHours[i], 'Date', '');
            if (date === todaysDate) {
                return venueHours[i];
            }
        }
    }
    timeOfDay(openTime, closeTime) {
        const a = moment.tz(openTime, this.timeZone).format('a');
        const b = moment.tz(closeTime, this.timeZone).format('a');
        return a === b ? a : a + '-' + b;
    }
    replaceVenueHoursHashCodes(content, hoursData, timezone, extraHashCodes) {
        const venueStatus = get(hoursData, 'VenueStatus', '');
        const openTime = DateTime.fromISO(get(hoursData, 'OpenTimeString', '')).setZone(timezone);
        const closeTime = DateTime.fromISO(get(hoursData, 'CloseTimeString', '')).setZone(timezone);
        let parkHoursText = '';
        const earlyEntryTime = DateTime.fromISO(get(hoursData, 'EarlyEntryString', '')).setZone(timezone);
        let earlyEntryText = get(this.pageLabels, 'notAvailable', '');
        let earlyEntryOpenTimeText = get(this.pageLabels, 'notAvailable', '');
        const specialEntryTime = DateTime.fromISO(get(hoursData, 'SpecialEntryString', '')).setZone(timezone);
        let specialEntryText = get(this.pageLabels, 'notAvailable', '');
        if (!venueStatus) {
            if (openTime.isValid && closeTime.isValid) {
                parkHoursText = `${this.gdsCommonUtilities.createTimeElement(openTime)} - ${this.gdsCommonUtilities.createTimeElement(closeTime)}`;
            }
            if (earlyEntryTime.isValid) {
                earlyEntryText =
                    this.gdsCommonUtilities.createTimeElement(earlyEntryTime);
            }
            if (earlyEntryTime.isValid && openTime.isValid) {
                earlyEntryOpenTimeText =
                    `${this.gdsCommonUtilities.createTimeElement(earlyEntryTime)}
									- ${this.gdsCommonUtilities.createTimeElement(openTime)}`;
            }
            if (specialEntryTime.isValid) {
                specialEntryText =
                    this.gdsCommonUtilities.createTimeElement(specialEntryTime);
            }
        }
        if (venueStatus) {
            parkHoursText = get(this.pageLabels, venueStatus, null) ?? venueStatus;
        }
        return this.gdsCommonUtilities.replaceHashCodes(content, {
            parkhours: parkHoursText,
            earlyentry: earlyEntryText,
            specialentry: specialEntryText,
            earlyentrytoopenhours: earlyEntryOpenTimeText,
            ...extraHashCodes,
        });
    }
    async setHours(componentData, sectionIndex, hoursInfo, venueStatus) {
        const sectionObject = get(componentData, `section[${sectionIndex}]`) ||
            get(componentData, `section`);
        const secondSectionObject = get(componentData, 'secondSection');
        const section = sectionObject || secondSectionObject;
        const description = get(section, 'description');
        if (!description) {
            return;
        }
        const label = this.pageLabels[hoursInfo]
            ? this.pageLabels[hoursInfo]
            : hoursInfo;
        const parkhours = venueStatus
            ? label
            : description.replace('##Parkhours##', hoursInfo);
        if (get(componentData, 'secondSection')) {
            set(componentData, 'secondSection[description]', parkhours);
        }
        else if (get(componentData, `section`)) {
            if (get(componentData, `section[${sectionIndex}]`)) {
                set(componentData, `section[${sectionIndex}].description`, parkhours);
            }
            else {
                set(componentData, 'section[description]', parkhours);
            }
        }
        return componentData;
    }
    getRelatedTiles(pagePath) {
        this.subscriptions.add(this.fullImageCardService.items$
            .pipe(distinctUntilChanged())
            .subscribe((data) => {
            if (data) {
                this.relatedTiles$.next(data);
            }
        }));
        this.fullImageCardService.init(pagePath);
    }
    ngOnDestroy() {
        return;
    }
};
GdsPoiService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsPoiService, deps: [{ token: i0.Injector }, { token: i1.HttpClient }, { token: i2.GdsCommonUtilityService }, { token: i3.LocaleService }, { token: i4.PageLabelsResolver }, { token: i5.SessionStorageService }, { token: i6.MsPlacesService }, { token: i6.MsPlacesCommonUtilityService }, { token: 'AppDefaults' }], target: i0.ɵɵFactoryTarget.Injectable });
GdsPoiService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsPoiService, providedIn: 'root' });
GdsPoiService = __decorate([
    AutoSubscription(),
    __metadata("design:paramtypes", [Injector,
        HttpClient,
        GdsCommonUtilityService,
        LocaleService,
        PageLabelsResolver,
        SessionStorageService,
        MsPlacesService,
        MsPlacesCommonUtilityService, Object])
], GdsPoiService);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.4.0", ngImport: i0, type: GdsPoiService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }, { type: i1.HttpClient }, { type: i2.GdsCommonUtilityService }, { type: i3.LocaleService }, { type: i4.PageLabelsResolver }, { type: i5.SessionStorageService }, { type: i6.MsPlacesService }, { type: i6.MsPlacesCommonUtilityService }, { type: undefined, decorators: [{
                    type: Inject,
                    args: ['AppDefaults']
                }] }]; } });

export { GdsPoiModule, GdsPoiService };
